/**
 * Sometimes it is required to update the default texts Usercentrics provides for default services. The current strategy
 * to do this is to create equivalent custom services with the same name containing the customized texts.
 * This solution lead to a problem in the consent handling in the dataLayer, in which only service names are taken
 * into account. To address this, for each pair of services (duplicate and customized with same name), only the customized
 * one is shown. Later, during the user input save process, the option for customized one is synchronized with its equivalent
 * default. This way, the consent status for two services with same name will be always the same.
 */

import { BaseCategory, UserDecision } from "@usercentrics/cmp-browser-sdk";
import { UsercentricsServiceId } from "./CmpModel";

interface ServiceCustomization {
  customServiceId: string;
  defaultServiceId: string;
}

const servicesEquivalenceMap = new Map<string, ServiceCustomization>([
  [
    "Google Analytics",
    {
      customServiceId:
        UsercentricsServiceId.GOOGLE_ANALYTICS_CUSTOMIZED_SERVICE_ID,
      defaultServiceId:
        UsercentricsServiceId.GOOGLE_ANALYTICS_DEFAULT_SERVICE_ID
    }
  ],
  [
    "Google Optimize",
    {
      customServiceId:
        UsercentricsServiceId.GOOGLE_OPTIMIZED_CUSTOMIZED_SERVICE_ID,
      defaultServiceId:
        UsercentricsServiceId.GOOGLE_OPTIMIZED_DEFAULT_SERVICE_ID
    }
  ],
  [
    "Google CrashLytics",
    {
      customServiceId:
        UsercentricsServiceId.GOOGLE_CRASHLYTICS_CUSTOMIZED_SERVICE_ID,
      defaultServiceId:
        UsercentricsServiceId.GOOGLE_CRASHLYTICS_DEFAULT_SERVICE_ID
    }
  ],
  [
    "Google Firebase",
    {
      customServiceId:
        UsercentricsServiceId.GOOGLE_FIREBASE_CUSTOMIZED_SERVICE_ID,
      defaultServiceId: UsercentricsServiceId.GOOGLE_FIREBASE_DEFAULT_SERVICE_ID
    }
  ],
  [
    "Google RTB House",
    {
      customServiceId:
        UsercentricsServiceId.GOOGLE_RTBHOUSE_CUSTOMIZED_SERVICE_ID,
      defaultServiceId: UsercentricsServiceId.GOOGLE_RTBHOUSE_DEFAULT_SERVICE_ID
    }
  ]
]);
const servicesEquivalenceArray = Array.from(servicesEquivalenceMap.values());

export function hideDefaultServices(categories: BaseCategory[]) {
  categories.forEach(
    category =>
      (category.services = category.services.filter(
        service => !isDefaultService(service.id)
      ))
  );
}

export function addDefaultServicesEquivalentDecisions(
  decisions: UserDecision[]
): UserDecision[] {
  const defaultServicesStatus: UserDecision[] = decisions
    .filter(decision => hasDefaultServiceAssociated(decision.serviceId))
    .map(decision => buildDecisionForEquivalentDefaultService(decision));

  return decisions.concat(defaultServicesStatus);
}

function isDefaultService(serviceId: string): boolean {
  return servicesEquivalenceArray
    .map(service => service.defaultServiceId)
    .includes(serviceId);
}

function hasDefaultServiceAssociated(decisionServiceId: string): boolean {
  return servicesEquivalenceArray
    .map(service => service.customServiceId)
    .includes(decisionServiceId);
}

function buildDecisionForEquivalentDefaultService(
  decision: UserDecision
): UserDecision {
  const id = retrieveDefaultServiceIdForCustomServiceId(decision.serviceId);
  const status = decision.status;
  return { serviceId: id, status: status };
}

function retrieveDefaultServiceIdForCustomServiceId(
  customServiceId: string
): string {
  return servicesEquivalenceArray.find(
    it => it.customServiceId === customServiceId
  )!.defaultServiceId;
}
